import type { I18n } from '@funkey/locales';
import { std } from '@funkey/shared';
import { Units } from '~/modules/units';
import { formatDurationRange } from '../shared';

/**
 * Pure logic for the "active filter pills" shown above the activity search results.
 *
 * Extracted from `client/index.tsx` so the labeling/grouping/dedup algorithm can be
 * unit-tested in isolation. The `ActiveFilterPills` React component (hooks + JSX)
 * stays in `index.tsx` and consumes `buildActiveFilterPills`.
 */

/** A category/region/language entry, structurally typed to only the fields read here. */
type LookupEntry = { id: string | number; name?: string | null; filterName?: string | null };

export type FilterPillLookupData = {
    categories: { categories?: ReadonlyArray<LookupEntry> | null };
    regions: { regions?: ReadonlyArray<{ id: string | number; name?: string | null }> | null };
    languages: { languages?: ReadonlyArray<{ id: string | number; name?: string | null }> | null };
};

type ActiveFilterPill = {
    label: string;
    keys: string[];
    /** The filter update that removes this pill. For a multi-select filter that
     * is the remaining values, not `null`: taking one category off leaves the
     * others standing. */
    remove: Record<string, unknown>;
};

/** What makes a pill the same pill as another while they are being collected.
 * `categories` and `subCategories` share the generic "Categories" label and are
 * meant to merge; two selected values that happen to translate to the same word
 * are not, so those carry their own value here. Dropped before returning: it is
 * bookkeeping, not part of a pill. */
type CollectedPill = ActiveFilterPill & { mergeKey?: string };

type ActiveFilterPillI18n = Pick<I18n, '_' | 'locale'>;

const FILTER_LABELS: Record<string, (i18n: ActiveFilterPillI18n) => string> = {
    categories: i18n => i18n._('Categories'),
    subCategories: i18n => i18n._('Categories'),
    regions: i18n => i18n._('Region'),
    minBudget: i18n => i18n._('Budget'),
    maxBudget: i18n => i18n._('Budget'),
    participants: i18n => i18n._('Participant count'),
    languages: i18n => i18n._('Language'),
    season: i18n => i18n._('Season'),
    location: i18n =>
        i18n._('activity.filter.location.title', undefined, {
            message: 'Location',
        }),
    // The place someone typed into the location filter. Only the label carries a
    // pill; the coordinates and the radius ride along in its group so removing it
    // drops the whole distance search rather than half of it.
    nearLabel: i18n =>
        i18n._('activity.filter.location.title', undefined, {
            message: 'Location',
        }),
    locationType: i18n =>
        i18n._('activity.filter.location_type.title', undefined, {
            message: 'Location type',
        }),
    minDuration: i18n => i18n._('Duration'),
    maxDuration: i18n => i18n._('Duration'),
    cleanliness: i18n => i18n._('Cleanliness'),
    teamwork: i18n => i18n._('Traits'),
    action: i18n => i18n._('Traits'),
    competition: i18n => i18n._('Traits'),
    learning: i18n => i18n._('Traits'),
    // Commented out: accessibility & doelgroep (re-enable after merge)
    // accessibility: i18n =>
    //     i18n._('activity.filter.accessibility_and_audience.title', undefined, {
    //         message: 'Accessibility',
    //     }),
    // targetAudience: i18n =>
    //     i18n._('activity.filter.accessibility_and_audience.title', undefined, {
    //         message: 'Accessibility',
    //     }),
    query: i18n => i18n._('Search'),
};

/** Groups of filter keys that should be displayed as a single pill */
// A group is one pill that clears every key in it. Only the distance search is
// grouped: its label, coordinates and radius are one thing to a visitor. Budget,
// duration and the traits each keep a pill per value, so dropping one end or one
// trait does not take the rest with it.
const FILTER_GROUPS: Record<string, string[]> = {
    NearPoint: ['nearLabel', 'nearLat', 'nearLng', 'radius'],
    // Commented out: accessibility & doelgroep (re-enable after merge)
    // Accessibility: ['accessibility', 'targetAudience'],
};

// Commented out accessibility & targetAudience from extra filters (re-enable after merge)
const EXTRA_FILTER_KEYS = new Set(['season', 'cleanliness']);

function toNumberArray(value: unknown): number[] {
    return Array.isArray(value) ? value.filter((v): v is number => typeof v === 'number') : [];
}

function toStringArray(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function getDescriptiveLabel(
    key: string,
    values: Record<string, unknown>,
    data: FilterPillLookupData,
    i18n: ActiveFilterPillI18n,
): string {
    const fallback = FILTER_LABELS[key]?.(i18n) ?? key;

    switch (key) {
        case 'participants': {
            const v = values.participants;
            if (typeof v === 'number') return i18n._('{count} participants', { count: v });
            return fallback;
        }
        case 'minBudget': {
            const min = typeof values.minBudget === 'number' ? values.minBudget : null;
            return min == null
                ? fallback
                : `${i18n._('From')} ${Units.formatEUR(min * 100, i18n.locale)}`;
        }
        case 'maxBudget': {
            const max = typeof values.maxBudget === 'number' ? values.maxBudget : null;
            return max == null
                ? fallback
                : `${i18n._('Up to')} ${Units.formatEUR(max * 100, i18n.locale)}`;
        }
        case 'minDuration':
        case 'maxDuration': {
            const value = typeof values[key] === 'number' ? values[key] : null;
            if (value == null) return fallback;
            // Same unit choice as the cards, so "45 min" and "2u 30m" never mix.
            // Both ends go through one range so a pair keeps one unit between them.
            const range = formatDurationRange(i18n.locale, {
                min: typeof values.minDuration === 'number' ? values.minDuration : value,
                max: typeof values.maxDuration === 'number' ? values.maxDuration : value,
            });
            const parts = key === 'minDuration' ? range.minParts : range.maxParts;
            const prefix = key === 'minDuration' ? i18n._('From') : i18n._('Up to');

            if (range.unit === 'mixed') {
                const shown =
                    parts.hours === 0
                        ? i18n._('activity.card.durationMinutes', { value: parts.minutes })
                        : i18n._('activity.card.durationHoursMinutes', parts);
                return `${prefix} ${shown}`;
            }

            const unitKey =
                range.unit === 'hours'
                    ? 'activity.card.durationHours'
                    : 'activity.card.durationMinutes';
            return `${prefix} ${i18n._(unitKey, { value: key === 'minDuration' ? range.min : range.max })}`;
        }
        case 'categories':
        case 'subCategories': {
            const allIds = [
                ...toNumberArray(values.categories),
                ...toNumberArray(values.subCategories),
            ].map(String);
            const names = allIds
                .map(id => {
                    const cat = data.categories.categories?.find(c => String(c.id) === id);
                    return cat?.filterName ?? cat?.name;
                })
                .filter(Boolean);
            if (names.length > 0) return names.join(', ');
            return fallback;
        }
        case 'regions': {
            const names = toNumberArray(values.regions)
                .map(id => data.regions.regions?.find(r => Number(r.id) === id)?.name)
                .filter(Boolean);
            if (names.length > 0) return names.join(', ');
            return fallback;
        }
        case 'languages': {
            const names = toNumberArray(values.languages)
                .map(id => data.languages.languages?.find(l => Number(l.id) === id)?.name)
                .filter(Boolean);
            if (names.length > 0) return names.join(', ');
            return fallback;
        }
        case 'season': {
            const seasons = toStringArray(values.season);
            const SEASON_LABELS: Record<string, () => string> = {
                SPRING: () => i18n._('Spring'),
                SUMMER: () => i18n._('Summer'),
                FALL: () => i18n._('Fall'),
                WINTER: () => i18n._('Winter'),
            };
            const names = seasons.map(s => SEASON_LABELS[s]?.()).filter(Boolean);
            if (names.length > 0) return names.join(', ');
            return fallback;
        }
        case 'locationType': {
            const types = toStringArray(values.locationType);
            const TYPE_LABELS: Record<string, () => string> = {
                INDOOR: () => i18n._('Indoor'),
                OUTDOOR: () => i18n._('Outdoor'),
                VIRTUAL: () => i18n._('Online / Virtual'),
            };
            const names = types.map(t => TYPE_LABELS[t]?.()).filter(Boolean);
            if (names.length > 0) return names.join(', ');
            return fallback;
        }
        case 'cleanliness': {
            const vals = toStringArray(values.cleanliness);
            const LABELS: Record<string, () => string> = {
                CLEAN: () => i18n._('Clean'),
                WET: () => i18n._('Wet'),
                DIRTY: () => i18n._('Dirty'),
            };
            const names = vals.map(v => LABELS[v]?.()).filter(Boolean);
            if (names.length > 0) return names.join(', ');
            return fallback;
        }
        case 'location': {
            const vals = toStringArray(values.location);
            const LABELS: Record<string, () => string> = {
                FIXED: () =>
                    i18n._('activity.filter.location.option.fixed', undefined, {
                        message: 'I go to the partner',
                    }),
                CUSTOM: () =>
                    i18n._('activity.filter.location.option.custom', undefined, {
                        message: 'Location of choice',
                    }),
            };
            const names = vals.map(v => LABELS[v]?.()).filter(Boolean);
            if (names.length > 0) return names.join(', ');
            return fallback;
        }
        case 'teamwork':
        case 'action':
        case 'competition':
        case 'learning': {
            const TRAIT_LABELS: Record<string, () => string> = {
                teamwork: () => i18n._('Teamwork'),
                action: () => i18n._('Action'),
                competition: () => i18n._('Competition'),
                learning: () => i18n._('Learning'),
            };
            return TRAIT_LABELS[key]?.() ?? fallback;
        }
        // Commented out: accessibility & doelgroep (re-enable after merge)
        // case 'accessibility':
        // case 'targetAudience': {
        //     const accessibilityVals = values.accessibility as string[];
        //     const audienceVals = values.targetAudience as string[];
        //     const count = (accessibilityVals?.length ?? 0) + (audienceVals?.length ?? 0);
        //     if (count > 0) {
        //         return `${fallback} (${count})`;
        //     }
        //     return fallback;
        // }
        case 'nearLabel': {
            const label = typeof values.nearLabel === 'string' ? values.nearLabel : '';
            if (!label) return fallback;
            const radius = typeof values.radius === 'number' ? values.radius : null;
            return radius == null ? label : `${label} (${radius} km)`;
        }
        case 'query': {
            const q = typeof values.query === 'string' ? values.query : '';
            if (q) return `"${q}"`;
            return fallback;
        }
        default:
            return fallback;
    }
}

/**
 * The chosen values of a multi-select filter, one entry each, so every value can
 * carry its own pill. Returns `null` for filters that are not multi-select.
 */
function getValueEntries(
    key: string,
    values: Record<string, unknown>,
    data: FilterPillLookupData,
    i18n: ActiveFilterPillI18n,
): Array<{ label: string; value: string | number }> | null {
    const fromIds = (ids: number[], name: (id: number) => string | null | undefined) =>
        ids.map(id => ({ label: name(id) ?? '', value: id })).filter(entry => !!entry.label);

    const fromCodes = (codes: string[], labels: Record<string, () => string>) =>
        codes.map(code => ({ label: labels[code]?.() ?? '', value: code })).filter(e => !!e.label);

    switch (key) {
        case 'categories':
        case 'subCategories':
            return fromIds(toNumberArray(values[key]), id => {
                const cat = data.categories.categories?.find(c => String(c.id) === String(id));
                return cat?.filterName ?? cat?.name;
            });
        case 'regions':
            return fromIds(
                toNumberArray(values.regions),
                id => data.regions.regions?.find(r => Number(r.id) === id)?.name,
            );
        case 'languages':
            return fromIds(
                toNumberArray(values.languages),
                id => data.languages.languages?.find(l => Number(l.id) === id)?.name,
            );
        case 'season':
            return fromCodes(toStringArray(values.season), {
                SPRING: () => i18n._('Spring'),
                SUMMER: () => i18n._('Summer'),
                FALL: () => i18n._('Fall'),
                WINTER: () => i18n._('Winter'),
            });
        case 'locationType':
            return fromCodes(toStringArray(values.locationType), {
                INDOOR: () => i18n._('Indoor'),
                OUTDOOR: () => i18n._('Outdoor'),
                VIRTUAL: () => i18n._('Online / Virtual'),
            });
        case 'cleanliness':
            return fromCodes(toStringArray(values.cleanliness), {
                CLEAN: () => i18n._('Clean'),
                WET: () => i18n._('Wet'),
                DIRTY: () => i18n._('Dirty'),
            });
        case 'location':
            return fromCodes(toStringArray(values.location), {
                FIXED: () =>
                    i18n._('activity.filter.location.option.fixed', undefined, {
                        message: 'I go to the partner',
                    }),
                CUSTOM: () =>
                    i18n._('activity.filter.location.option.custom', undefined, {
                        message: 'Location of choice',
                    }),
            });
        default:
            return null;
    }
}

/**
 * Build the deduplicated list of active filter pills from the current filter values.
 * Pure — no React, no side effects.
 */
export function buildActiveFilterPills(args: {
    values: Record<string, unknown> & { categories: unknown[] };
    data: FilterPillLookupData;
    i18n: ActiveFilterPillI18n;
    showExtraFilters: boolean;
    /** The control page shows a chosen category as a chip row of its own, so
     * its pill would repeat it. The reworked page has no such row. */
    hasSubCategoryRow?: boolean;
}): ActiveFilterPill[] {
    const { values, data, i18n, showExtraFilters, hasSubCategoryRow = true } = args;
    const pills: CollectedPill[] = [];
    const seen = new Set<string>();

    const singleCategorySelected = hasSubCategoryRow && values.categories.length === 1;

    for (const [key, value] of Object.entries(std.Object.omit(values, ['sort', 'countryCode']))) {
        if (value === null || value === undefined) continue;
        if (typeof value === 'string' && value.length === 0) continue;
        if (Array.isArray(value) && value.length === 0) continue;
        if (seen.has(key)) continue;

        if (singleCategorySelected && (key === 'categories' || key === 'subCategories')) continue;

        // A city page starts at a place, so `nearLabel` is one of its defaults
        // and clearing the point cannot null it away — it comes straight back.
        // The flag is what says the visitor took it off, and it is what the
        // filter bar reads, so the pill row has to read it too or the two
        // disagree: the bar says "Overal" while a place pill sits under it.
        if (key === 'nearLabel' && values.nearCleared === true) continue;

        // Skip extra filters when feature flag is off
        if (!showExtraFilters && EXTRA_FILTER_KEYS.has(key)) continue;

        // Find label and group
        const labelFn = FILTER_LABELS[key];
        if (!labelFn) continue;

        // A multi-select filter gets a pill per chosen value. If none of them
        // resolve to a name the lookup is still loading, so fall back to the one
        // generic pill rather than dropping the filter off the row entirely.
        const entries = getValueEntries(key, values, data, i18n);
        if (entries && entries.length > 0) {
            seen.add(key);
            // The remaining values come off the selection itself, not off the
            // pills: a value the lookup could not name has no pill, and taking
            // it down with a neighbour would filter the page by something the
            // visitor never touched. Empty stays an empty array rather than
            // null — on a page that defaults to a region, null hands that
            // region straight back.
            const selected: readonly unknown[] = Array.isArray(values[key]) ? values[key] : [];
            for (const entry of entries) {
                pills.push({
                    label: entry.label,
                    keys: [key],
                    remove: { [key]: selected.filter(value => value !== entry.value) },
                    mergeKey: `${key}:${String(entry.value)}`,
                });
            }
            continue;
        }

        // Build descriptive label instead of generic one
        const label = getDescriptiveLabel(key, values, data, i18n);

        // Check if this key belongs to a group
        const group = Object.entries(FILTER_GROUPS).find(([, keys]) => keys.includes(key));
        if (group) {
            const [, groupKeys] = group;
            if (seen.has(groupKeys[0]!)) continue; // Already added this group
            // Check if any key in the group is active
            const hasActive = groupKeys.some(gk => {
                const v = values[gk];
                if (v === null || v === undefined) return false;
                if (typeof v === 'string' && v.length === 0) return false;
                if (Array.isArray(v) && v.length === 0) return false;
                return true;
            });
            if (!hasActive) continue;
            groupKeys.forEach(gk => seen.add(gk));
            // A region page prefills its own region AND a radius around it,
            // both carrying the region's name, so the row read "Luxemburg" and
            // "Luxemburg (50 km)" side by side. To a visitor that is one place:
            // sharing the region's merge key folds them into one pill that
            // takes both off, and the region's plainer label is the survivor.
            const sameRegion = data.regions.regions?.find(
                region =>
                    region.name === values.nearLabel &&
                    toNumberArray(values.regions).includes(Number(region.id)),
            );
            pills.push({
                label,
                keys: groupKeys,
                remove: std.Object.fromEntries(groupKeys.map(gk => [gk, null] as const)),
                mergeKey: sameRegion ? `regions:${sameRegion.id}` : undefined,
            });
        } else {
            seen.add(key);
            pills.push({ label, keys: [key], remove: { [key]: null } });
        }
    }

    // Merge the filters that are one filter to a visitor (subCategories and
    // categories both read "Categories"). Merging on the label alone would also
    // collapse two different selections that translate to the same word, and the
    // second one's remove payload would overwrite the first — leaving a filter
    // active with no pill left to switch it off.
    const deduped: CollectedPill[] = [];
    const mergeSeen = new Map<string, CollectedPill>();
    for (const pill of pills) {
        const mergeKey = pill.mergeKey ?? `label:${pill.label}`;
        const existing = mergeSeen.get(mergeKey);
        if (existing) {
            existing.keys.push(...pill.keys);
            existing.remove = { ...existing.remove, ...pill.remove };
            continue;
        }
        mergeSeen.set(mergeKey, pill);
        deduped.push(pill);
    }

    return deduped.map(pill => ({ label: pill.label, keys: pill.keys, remove: pill.remove }));
}
